import React, { useState } from "react";
import PropTypes from "prop-types";
import Toast from "../../components/toast";
import Select from "../../components/inputs/select";
import Button from "../../components/inputs/button";
import BulkProgressIndicator from "./BulkProgressIndicator";
import ModalSurface from "./ModalSurface";
import useBatchOperation from "../../hooks/useBatchOperation";
import { bulkEditCohorts, isConcurrentChange, versionsOf } from "../../services/cohort";
import {
	ttlOptions,
	BULK_CONFIRM_ESCALATION_THRESHOLD,
	BULK_EDIT_CHUNK_SIZE,
} from "../../utilities/constants";
import "./index.scss";

function chunkArray(arr, size) {
	const out = [];
	for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
	return out;
}

function BulkTtlEdit({ cohort, tagCohorts, getCohorts, onUpdate }) {
	const uniqueIds = cohort ? cohort.map((c) => c.value) : [];
	const count = uniqueIds.length;

	const [selectedOption, setSelectedOption] = useState(null);
	const [toast, setToast] = useState(null);
	const [loading, setLoading] = useState(false);

	const { execute, cancel, state: batchState } = useBatchOperation({ batchSize: 1 });

	const escalate = count > BULK_CONFIRM_ESCALATION_THRESHOLD;
	const audienceWord = count !== 1 ? "audiences" : "audience";

	const handleUpdate = async () => {
		if (!selectedOption) return;
		setLoading(true);
		const tagId = localStorage.getItem("activeSilo");

		try {
			const chunks = chunkArray(uniqueIds, BULK_EDIT_CHUNK_SIZE);
			const tasks = chunks.map((chunkIds, i) => ({
				id: `chunk-${i}`,
				run: () => bulkEditCohorts(chunkIds, { ttl: selectedOption.value }, tagId, versionsOf(cohort.filter((c) => chunkIds.includes(c.value)))),
			}));

			const result = await execute(tasks);

			const cohortsSucceeded = Math.min(result.succeeded * BULK_EDIT_CHUNK_SIZE, count);
			const cohortsFailed = count - cohortsSucceeded;
			// ASB-4801: a part refused because someone else saved one of its audiences since the
			// list loaded says so in the API's words; closing the toast reloads the list.
			const changed = (result.errors || []).find(isConcurrentChange);
			const changedNote = changed ? ` ${changed.data.message}` : "";

			if (result.cancelled) {
				setLoading(false);
				setToast({
					message: `Operation cancelled. TTL updated for ${cohortsSucceeded} of ${count} ${audienceWord}.`,
					status: "Invalid",
					title: "Cancelled",
				});
				return;
			}

			// Reload now, not when the toast closes: saved rows' versions moved (ASB-4801).
			if (result.succeeded > 0) getCohorts(tagId);
			if (result.failed === 0) {
				setLoading(false);
				setToast({
					message: `Updated TTL for ${count} ${audienceWord} to ${selectedOption.label}`,
					status: "Success",
					title: "Success",
				});
			} else if (result.succeeded === 0) {
				setLoading(false);
				setToast({
					message: changed ? changed.data.message : "Error updating audiences",
					status: "Invalid",
					title: changed ? "Not saved" : "Error",
				});
			} else {
				setLoading(false);
				setToast({
					message: `Updated TTL for ${cohortsSucceeded} of ${count} ${audienceWord}. ${cohortsFailed} failed.${changedNote}`,
					status: "Invalid",
					title: "Partial Success",
				});
			}
		} catch (error) {
			setLoading(false);
			setToast({
				message: error.message || "Error updating audiences",
				status: "Invalid",
				title: "Error",
			});
		}
	};

	const postUpdate = () => {
		getCohorts(localStorage.getItem("activeSilo"));
		onUpdate();
		// After a failure the selection stays, so the user can retry, as for archive (ASB-4801).
		if (toast?.status !== "Invalid") tagCohorts("");
	};

	const toastClose = () => {
		setToast(null);
		postUpdate();
	};

	const confirmCopy = escalate
		? (selectedOption
			? `You are about to change TTL for ${count} ${audienceWord} to ${selectedOption.label}. This will overwrite each audience's current TTL. Continue?`
			: `You are about to change TTL for ${count} ${audienceWord}. This will overwrite each audience's current TTL.`)
		: (selectedOption
			? `Set TTL to ${selectedOption.label} for ${count} ${audienceWord}?`
			: `Set TTL for ${count} ${audienceWord}.`);

	const progressCohortsCompleted = Math.min(batchState.completed * BULK_EDIT_CHUNK_SIZE, count);
	const progressLabel = `TTL updated for ${progressCohortsCompleted} of ${count} ${audienceWord}`;

	if (toast) {
		return (
			<Toast
				status={toast.status}
				message={toast.message}
				title={toast.title}
				callback={() => toastClose()}
				autoHide={false}
			/>
		);
	}

	const footer = (
		<>
			<button
				type="button"
				className="cancel-link"
				onClick={() => onUpdate()}
				disabled={loading}
			>
				Cancel
			</button>
			<Button
				loaded={loading}
				onClick={handleUpdate}
				text="Save Changes"
				style="dark"
				type="button"
				disabled={loading || !selectedOption}
			/>
		</>
	);

	return (
		<ModalSurface title="Update TTL" onClose={onUpdate} footer={footer} closeDisabled={loading}>
			<p className="p3">{confirmCopy}</p>
			<div className="modal-field">
				<Select
					name="bulk-ttl"
					label="Time to Live"
					selected={selectedOption}
					onSubmit={(option) => setSelectedOption(option)}
					options={ttlOptions}
					required={true}
					disabled={loading}
					search={true}
					menuPortal={true}
				/>
			</div>
			<BulkProgressIndicator state={batchState} onCancel={cancel} label={progressLabel} />
		</ModalSurface>
	);
}

BulkTtlEdit.propTypes = {
	cohort: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string,
		})
	).isRequired,
	tagCohorts: PropTypes.func.isRequired,
	getCohorts: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
};

export { BulkTtlEdit };
